import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Stack,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Campaign as AdsIcon,
  Refresh as RefreshIcon,
  LocationOn as LocationIcon,
  Store as StoreIcon,
  CalendarToday as CalendarIcon,
  OpenInNew as OpenInNewIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { format, isAfter, isBefore } from 'date-fns';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const UserAdsView = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewDialog, setViewDialog] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    document.title = 'Advertisements - LOCSY User Dashboard';
    return () => {
      document.title = 'LOCSY Admin Dashboard';
    };
  }, []);

  // Fetch advertisements for users
  // Note: This assumes an /ads endpoint exists. If not, you may need to create it or use a different endpoint
  const { data, isLoading, error, refetch } = useQuery(
    ['user-ads', page, search, typeFilter, lastRefresh],
    async () => {
      const params = {
        page,
        limit: 12,
        status: 'active',
        type: typeFilter !== 'all' ? typeFilter : '',
      };

      if (search) {
        params.search = search;
      }

      try {
        // Try to fetch from ads endpoint, fallback to empty if endpoint doesn't exist
        const response = await axios.get(`${API_BASE_URL}/ads`, {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        return response.data;
      } catch (err) {
        // If endpoint doesn't exist, return empty data
        if (err.response?.status === 404) {
          return { ads: [], total: 0, pagination: { totalPages: 0 } };
        }
        throw err;
      }
    },
    {
      refetchInterval: 60000, // Auto-refresh every minute
      refetchOnWindowFocus: true,
      retry: 1, // Only retry once
    }
  );

  const handleRefresh = () => {
    setLastRefresh(new Date());
    refetch();
    toast.info('Advertisements refreshed');
  };

  const adTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'pamphlet', label: 'Pamphlet' },
    { value: 'voiceAd', label: 'Voice Ad' },
    { value: 'hoarding', label: 'Hoarding' },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && error.response?.status !== 404) {
    return <Alert severity="error">Failed to load advertisements. Please try again.</Alert>;
  }

  const ads = data?.ads || data?.data || [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0.5 }}>
            Advertisements
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Explore local businesses and special promotions
          </Typography>
        </Box>
        <IconButton onClick={handleRefresh} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Filters */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder="Search advertisements..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                  {adTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Ads Grid */}
      <Grid container spacing={3}>
        {ads.map((ad) => {
          const isValid = ad.validTo && isAfter(new Date(ad.validTo), new Date());
          const isExpired = ad.validTo && isBefore(new Date(ad.validTo), new Date());

          return (
            <Grid item xs={12} sm={6} md={4} key={ad.id || ad.ad_id}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                  opacity: isExpired ? 0.6 : 1,
                }}
              >
                {/* Ad Image */}
                {ad.imageUrl ? (
                  <CardMedia
                    component="img"
                    height="200"
                    image={ad.imageUrl}
                    alt={ad.title}
                    sx={{ objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 200,
                      bgcolor: 'grey.200',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ImageIcon sx={{ fontSize: 64, color: 'grey.400' }} />
                  </Box>
                )}

                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>
                      {ad.title}
                    </Typography>
                    {ad.type && (
                      <Chip
                        label={ad.type}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {ad.shopName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <StoreIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {ad.shopName}
                      </Typography>
                    </Box>
                  )}

                  {ad.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                      {ad.description.length > 100
                        ? `${ad.description.substring(0, 100)}...`
                        : ad.description}
                    </Typography>
                  )}

                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {ad.validTo && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon fontSize="small" color="primary" />
                        <Typography variant="body2">
                          Valid until {format(new Date(ad.validTo), 'MMM dd, yyyy')}
                        </Typography>
                      </Box>
                    )}

                    {ad.location && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon fontSize="small" color="primary" />
                        <Typography variant="body2">{ad.location}</Typography>
                      </Box>
                    )}

                    {ad.viewCount !== undefined && (
                      <Typography variant="body2" color="text.secondary">
                        {ad.viewCount} views
                      </Typography>
                    )}
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  {/* Actions */}
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => setViewDialog(ad)}
                    >
                      View Details
                    </Button>
                    {ad.link && (
                      <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        href={ad.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<OpenInNewIcon />}
                      >
                        Visit
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Empty State */}
      {ads.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
          <AdsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No advertisements available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later for new promotions and local business offers
          </Typography>
        </Paper>
      )}

      {/* Pagination */}
      {data?.pagination && data.pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={data.pagination.totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* View Details Dialog */}
      <Dialog open={!!viewDialog} onClose={() => setViewDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdsIcon color="primary" />
            <Typography variant="h6">{viewDialog?.title}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewDialog && (
            <Stack spacing={2}>
              {viewDialog.imageUrl && (
                <CardMedia
                  component="img"
                  height="300"
                  image={viewDialog.imageUrl}
                  alt={viewDialog.title}
                  sx={{ objectFit: 'cover', borderRadius: 2 }}
                />
              )}

              {viewDialog.description && (
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body2">{viewDialog.description}</Typography>
                </Box>
              )}

              <Grid container spacing={2}>
                {viewDialog.shopName && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Business Name
                    </Typography>
                    <Typography variant="body2">{viewDialog.shopName}</Typography>
                  </Grid>
                )}
                {viewDialog.type && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Ad Type
                    </Typography>
                    <Typography variant="body2">{viewDialog.type}</Typography>
                  </Grid>
                )}
                {viewDialog.validFrom && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Valid From
                    </Typography>
                    <Typography variant="body2">
                      {format(new Date(viewDialog.validFrom), 'MMM dd, yyyy')}
                    </Typography>
                  </Grid>
                )}
                {viewDialog.validTo && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Valid Until
                    </Typography>
                    <Typography variant="body2">
                      {format(new Date(viewDialog.validTo), 'MMM dd, yyyy')}
                    </Typography>
                  </Grid>
                )}
                {viewDialog.location && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Location
                    </Typography>
                    <Typography variant="body2">{viewDialog.location}</Typography>
                  </Grid>
                )}
                {viewDialog.viewCount !== undefined && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Views
                    </Typography>
                    <Typography variant="body2">{viewDialog.viewCount}</Typography>
                  </Grid>
                )}
              </Grid>

              {viewDialog.link && (
                <Box>
                  <Button
                    variant="contained"
                    fullWidth
                    href={viewDialog.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<OpenInNewIcon />}
                  >
                    Visit Business
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserAdsView;







